import React, { useCallback, useEffect } from "react";
import moment from "moment";
import { useAdminContext } from "../../context/AdminContext";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import CustomTable, { Column } from "../../components/CustomTable";
import { userListModel } from "../../model/adminModel";
import { useLoading } from "../../context/LoadingContext";
import { useNavigate, useLocation } from "react-router-dom";
import { FcSearch } from "react-icons/fc";
import StaticModal from "../../components/staticModel";

const UserPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { isLoading } = useLoading();
  const { listUser, getAllUser, setListUser } = useAdminContext();
  const [SearchByName, setSearchByName] = React.useState<string>("");
  const [userProjectList, setProjectUserList] = React.useState<
    | {
        project_name: string;
        status: string;
        project_startdate: string;
        project_enddate: string;
        employeeLdap: string;
        ts_approver_name: string;
        project_id: number;
        client_id: number;
      }[]
    | null
  >(null);

  const navigate = useNavigate();
  const location = useLocation();

  const columns: Column<userListModel>[] = [
    { header: "Employee Id", key: "employee_id", enableLink: false, path: "" },
    {
      header: "Employee Name",
      key: "ldap_uid",
      enableLink: true,
      path: "/admin/user/update",
    },
    {
      header: "Email",
      key: "email_id",
      enableLink: false,
      path: "",
    },
    { header: "Date Of Joining", key: "doj", enableLink: false, path: "" },
    { header: "Designation", key: "designation", enableLink: false, path: "" },
    { header: "Role", key: "role", enableLink: false, path: "" },
    {
      header: "Manager Name",
      key: "manager_name",
      enableLink: false,
      path: "",
    },
    {
      header: "Projects",
      key: "projects_dummy",
      enableLink: false,
      viewButton: true,
      path: "",
    },
    { header: "Status", key: "status", enableLink: false, path: "" },
  ];

  const getUser = useCallback((search: string) => {
    const payload = {
      search: search,
    };
    getAllUser(payload);
  }, []);

  // Single mount effect: restore modal and search state
  useEffect(() => {
    let savedState: any = null;

    // Priority 1: Check navigation state (returning from detail page)
    if (location.state?.userPageFilters) {
      savedState = location.state.userPageFilters;
    } else {
      // Priority 2: Check sessionStorage
      const sessionState = sessionStorage.getItem("userPageState");
      if (sessionState) {
        try {
          savedState = JSON.parse(sessionState);
        } catch (e) {
          savedState = null;
        }
      }
    }

    if (savedState) {
      const {
        searchByName,
        isModalOpen: wasModalOpen,
        userProjects,
      } = savedState;

      // Restore search value
      if (searchByName) {
        setSearchByName(searchByName);
        // Re-fetch users with the saved search
        getUser(searchByName);
      }

      // Restore modal state and projects
      if (wasModalOpen && userProjects) {
        setProjectUserList(userProjects);
        setIsModalOpen(true);
      }

      // Save to sessionStorage for future back button clicks
      sessionStorage.setItem("userPageState", JSON.stringify(savedState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userList = React.useMemo(() => {
    return listUser
      ? listUser.map((item) => {
          return {
            ...item,
            doj: moment(item.doj).isValid()
              ? moment(item.doj).format("DD/MM/YYYY")
              : null,
            employeename: item.firstname + " " + item.lastname,
            status: item.status && item.status == 1 ? "Active" : "Inactive",
          };
        })
      : null;
  }, [listUser]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const refreshTable = () => {
    setListUser(null);
    setSearchByName("");
    // Clear sessionStorage when explicitly refreshing
    sessionStorage.removeItem("userPageState");
  };

  const handleSearch = (searchValueData: string) => {
    getUser(searchValueData);
  };

  const handleNavigation = (path: string | undefined, states: number) => {
    navigate(path ?? "", { state: { id: states, formtype: "update" } });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setProjectUserList(null);
    setIsModalOpen(false);
  };

  const handleView = (data: userListModel) => {
    openModal();
    console.log(data);
    setProjectUserList(data.projects);
  };

  // Function to handle clicking on a project link
  const handleProjectClick = (
    clientId?: number,
    projectId?: number,
    employeeLdap?: string,
  ) => {
    if (!clientId || !projectId || !employeeLdap) return;

    // Save current state before navigating away
    const stateToSave = {
      searchByName: SearchByName,
      isModalOpen: true,
      userProjects: userProjectList,
    };
    sessionStorage.setItem("userPageState", JSON.stringify(stateToSave));

    closeModal();

    navigate("/client/updateclientproject", {
      state: {
        clientId: clientId,
        projectId: projectId,
        id: projectId,
        formtype: "update",
        activeTab: "Team Members",
        openUserModal: true,
        targetEmployeeLdap: employeeLdap,
        returnPath: "/admin/user",
        userPageFilters: stateToSave,
        // closeUserModal: true,
      },
    });
  };

  return (
    <div>
      <CustomTable
        showFilter={false}
        columns={columns}
        idKeyName="id"
        data={userList ?? []}
        viewButton={true}
        handleNavigation={handleNavigation}
        handleView={(data: userListModel) => handleView(data)}
      >
        <button
          onClick={() =>
            navigate("/admin/user/create", {
              state: { id: null, formtype: "create" },
            })
          }
          className="flex items-center px-4 py-2 text-sm mb-3 bg-gray-900 text-white rounded-lg focus:outline-none hover:bg-gray-600"
        >
          Add Employee
        </button>
        <div>
          <div className="max-w-4xl gap-x-6 gap-y-8 grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ">
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
              >
                Employee Name
              </label>
              <div className="mt-2">
                <input
                  value={SearchByName}
                  onChange={(e) => {
                    setSearchByName(e.target.value);
                  }}
                  className="shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light"
                />
              </div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => handleSearch(SearchByName)}
                className="flex  items-center px-4 py-2 mt-9 text-sm border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-900 hover:bg-gray-200 text-white rounded-lg focus:outline-none "
              >
                <FcSearch className="h-5 w-5 text-white" />
              </button>
              <button
                onClick={() => refreshTable()}
                className="flex  items-center px-4 py-2 mt-9 text-sm bg-blue-700 hover:bg-blue-800 text-white rounded-lg focus:outline-none "
              >
                <ArrowPathIcon className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </CustomTable>

      <StaticModal
        headerName={"Project List"}
        isOpen={isModalOpen}
        onClose={closeModal}
      >
        <div style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
          <h4 className="py-2 font-bold text-black">Ongoing</h4>
          {userProjectList &&
            userProjectList.length !== 0 &&
            userProjectList
              .filter((items) => items.status === "active")
              .map((item) => (
                <span
                  key={
                    item.project_id || item.project_name || item.employeeLdap
                  }
                >
                  &emsp;
                  <button
                    type="button"
                    onClick={() =>
                      handleProjectClick(
                        item.client_id,
                        item.project_id,
                        item.employeeLdap,
                      )
                    }
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium p-0 bg-transparent border-0 cursor-pointer text-left inline"
                  >
                    {item.project_name}
                  </button>
                  {" - since "}
                  {new Date(item.project_startdate).toLocaleDateString(
                    "en-IN",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    },
                  )}
                  {item.ts_approver_name
                    ? ` (TS Approver: ${item.ts_approver_name})`
                    : ""}
                  <br />
                </span>
              ))}

          <h4 className="py-2 font-bold text-black">Completed</h4>
          {userProjectList &&
            userProjectList.length !== 0 &&
            userProjectList
              .filter((items) => items.status === "inactive")
              .map((item) => (
                <span
                  key={
                    item.project_id || item.project_name || item.employeeLdap
                  }
                >
                  &emsp;
                  <button
                    type="button"
                    onClick={() =>
                      handleProjectClick(
                        item.client_id,
                        item.project_id,
                        item.employeeLdap,
                      )
                    }
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium p-0 bg-transparent border-0 cursor-pointer text-left inline"
                  >
                    {item.project_name}
                  </button>
                  {" - "}
                  {new Date(item.project_startdate).toLocaleDateString(
                    "en-IN",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    },
                  )}
                  {" to "}
                  {new Date(item.project_enddate).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                  {item.ts_approver_name
                    ? ` (TS Approver: ${item.ts_approver_name})`
                    : ""}
                  <br />
                </span>
              ))}
        </div>
      </StaticModal>
    </div>
  );
};

export default UserPage;
